import { useMemo, useState } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import {
  HELPER_BLOCKER_REASONS,
  HELPER_BLOCKER_REASON_LABELS,
  type HelperBlockerReason,
} from '../../types/helperBlocker'
import type { HelperReadinessModel } from './helperReadinessModel'
import { useNls } from '../../nls/nlsProvider'
import { usePushBackHandler } from '../../context/BackStackContext'
import { FloatingInput } from '../Common/FloatingInput'
import { Button, IconButton, Pressable, Textarea } from '../Common'

export type BlockerReportMode = 'BLOCKER' | 'REPLENISHMENT'

export const BlockerReportSheet = ({
  open,
  mode,
  readiness,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean
  mode: BlockerReportMode
  readiness: HelperReadinessModel | null
  busy?: boolean
  error?: string | null
  onClose: () => void
  onSubmit: (input: { reason: HelperBlockerReason; note: string }) => void
}) => {
  const { t } = useNls()
  usePushBackHandler(onClose, open && !busy)
  const [reason, setReason] = useState<HelperBlockerReason>(mode === 'REPLENISHMENT' ? 'MISSING_STOCK' : 'MISSING_INFO')
  const [note, setNote] = useState('')
  const shortages = useMemo(
    () => Object.entries(readiness?.stockShortagesById ?? {}),
    [readiness?.stockShortagesById],
  )

  if (!open) return null

  const isReplenishment = mode === 'REPLENISHMENT'
  return (
    <div
      className="fixed inset-0 z-[140] flex items-end justify-center px-4 pb-6 sm:items-center bg-[color-mix(in_srgb,var(--bg)_75%,transparent)]"
      onClick={() => { if (!busy) onClose() }}
      data-testid="helper-blocker-sheet"
    >
      <div
        className="flex w-full max-w-sm flex-col gap-4 rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-pop)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--warning)]">
              {isReplenishment ? <Inventory2RoundedIcon style={{ fontSize: 14 }} /> : <ReportGmailerrorredRoundedIcon style={{ fontSize: 14 }} />}
              {isReplenishment ? t('NLSHelperBlockerNeedMoreTitle') : t('NLSHelperBlockerReportTitle')}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-[var(--muted)]">
              {isReplenishment ? t('NLSHelperBlockerNeedMoreBody') : t('NLSHelperBlockerReportBody')}
            </p>
          </div>
          <IconButton
            variant="tertiary"
            size="md"
            label={t('NLSCommonActionClose')}
            icon={<CloseRoundedIcon fontSize="small" />}
            className="shrink-0"
            onClick={onClose}
            disabled={busy}
          />
        </div>

        {shortages.length > 0 ? (
          <div className="rounded-2xl border border-[var(--warning)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] p-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
              {t('NLSHelperBlockerShortages')}
            </p>
            <div className="mt-2 flex flex-col gap-1 text-xs text-[var(--text-strong)]">
              {shortages.map(([id, shortage]) => (
                <span key={id}>
                  {shortage.name ?? id}: {shortage.available}/{shortage.required}{shortage.unit ? ` ${shortage.unit}` : ''}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {!isReplenishment ? (
          <div className="grid grid-cols-2 gap-2">
            {HELPER_BLOCKER_REASONS.map((entry) => (
              <Pressable
                key={entry}
                disabled={busy}
                aria-pressed={reason === entry}
                onClick={() => setReason(entry)}
                className={`min-h-10 rounded-2xl border px-3 py-2 text-left text-xs font-medium ${
                  reason === entry
                    ? 'border-[var(--warning)] bg-[color-mix(in_srgb,var(--warning)_14%,transparent)] text-[var(--text-strong)]'
                    : 'border-[var(--border-subtle)] bg-[var(--surface-muted)] text-[var(--muted)]'
                }`}
              >
                {HELPER_BLOCKER_REASON_LABELS[entry]}
              </Pressable>
            ))}
          </div>
        ) : null}

        <FloatingInput label={isReplenishment ? 'Replenishment details' : 'Blocker details'}>
          <Textarea
            value={note}
            onChange={setNote}
            disabled={busy}
            placeholder={isReplenishment ? t('NLSHelperBlockerNeedMorePlaceholder') : t('NLSHelperBlockerReportPlaceholder')}
            rows={4}
          />
        </FloatingInput>
        {error ? <p className="text-xs text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button
            variant="tertiary"
            size="md"
            onClick={onClose}
            disabled={busy}
          >
            {t('NLSCommonActionCancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => onSubmit({ reason: isReplenishment ? 'MISSING_STOCK' : reason, note })}
            disabled={busy || (!isReplenishment && !note.trim())}
          >
            {busy ? t('NLSHelperBlockerSending') : isReplenishment ? t('NLSHelperBlockerSendNeedMore') : t('NLSHelperBlockerSendReport')}
          </Button>
        </div>
      </div>
    </div>
  )
}
